/**
 * Cross-device language & theme preference sync (profiles ↔ localStorage).
 * DB errors are logged only — never throw into UI handlers.
 */

import { supabase } from '@/integrations/supabase/client';
import i18n from '@/i18n';
import {
  LANGUAGE_STORAGE_KEY,
  detectSupportedBrowserLanguage,
  getStoredLanguagePreference,
  normalizeLanguageCode,
  type LanguageCode,
} from '@/i18n/languages';
import {
  readThemeMode,
  type ThemeMode,
} from '@/utils/themePreference';

export type UserPreferencesRow = {
  preferred_language: string | null;
  preferred_theme: string | null;
};

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'system' || value === 'dark' || value === 'light';
}

export async function fetchUserPreferences(
  userId: string,
): Promise<UserPreferencesRow | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('preferred_language, preferred_theme')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.warn('[preferenceSync] fetchUserPreferences failed:', error.message);
      return null;
    }

    return data
      ? {
          preferred_language: data.preferred_language ?? null,
          preferred_theme: data.preferred_theme ?? null,
        }
      : null;
  } catch (err) {
    console.warn('[preferenceSync] fetchUserPreferences exception:', err);
    return null;
  }
}

export async function applyPreferencesLocally(
  prefs: UserPreferencesRow,
  applyThemeMode: (mode: ThemeMode) => void,
): Promise<void> {
  const lang = normalizeLanguageCode(prefs.preferred_language);
  if (lang) {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      await i18n.changeLanguage(lang);
    } catch (err) {
      console.warn('[preferenceSync] apply language locally failed:', err);
    }
  }

  if (isThemeMode(prefs.preferred_theme)) {
    try {
      applyThemeMode(prefs.preferred_theme);
    } catch (err) {
      console.warn('[preferenceSync] apply theme locally failed:', err);
    }
  }
}

async function upsertProfilePreferences(
  userId: string,
  patch: { preferred_language?: LanguageCode; preferred_theme?: ThemeMode },
): Promise<void> {
  const { data: updatedRows, error: updateError } = await supabase
    .from('profiles')
    .update(patch)
    .eq('user_id', userId)
    .select('user_id');

  if (updateError) {
    throw updateError;
  }

  if (updatedRows && updatedRows.length > 0) {
    return;
  }

  const { error: insertError } = await supabase.from('profiles').insert({
    user_id: userId,
    ...patch,
  });

  if (insertError) {
    throw insertError;
  }
}

export async function persistLanguageToDb(
  userId: string,
  lang: LanguageCode,
): Promise<void> {
  try {
    await upsertProfilePreferences(userId, { preferred_language: lang });
  } catch (err) {
    console.warn('[preferenceSync] persistLanguageToDb failed:', err);
  }
}

export async function persistThemeToDb(
  userId: string,
  theme: ThemeMode,
): Promise<void> {
  try {
    await upsertProfilePreferences(userId, { preferred_theme: theme });
  } catch (err) {
    console.warn('[preferenceSync] persistThemeToDb failed:', err);
  }
}

/**
 * On login: DB non-null values overwrite localStorage + UI.
 * Null columns get a one-time write-back from current local prefs.
 */
export async function syncPreferencesOnLogin(
  userId: string,
  options: { applyThemeMode: (mode: ThemeMode) => void },
): Promise<void> {
  try {
    const prefs = await fetchUserPreferences(userId);
    const dbLang = normalizeLanguageCode(prefs?.preferred_language);
    const dbTheme = isThemeMode(prefs?.preferred_theme)
      ? prefs!.preferred_theme
      : null;

    if (dbLang || dbTheme) {
      await applyPreferencesLocally(
        {
          preferred_language: dbLang,
          preferred_theme: dbTheme,
        },
        options.applyThemeMode,
      );
    }

    const writeBack: {
      preferred_language?: LanguageCode;
      preferred_theme?: ThemeMode;
    } = {};

    if (!dbLang) {
      writeBack.preferred_language =
        getStoredLanguagePreference() ?? detectSupportedBrowserLanguage();
    }
    if (!dbTheme) {
      writeBack.preferred_theme = readThemeMode();
    }

    if (Object.keys(writeBack).length > 0) {
      await upsertProfilePreferences(userId, writeBack);
    }
  } catch (err) {
    console.warn('[preferenceSync] syncPreferencesOnLogin failed:', err);
  }
}
